'use client'

import { useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'
import { isAnalyticsConsentGranted } from '@/lib/consent-manager'

/**
 * Tracks GA4 page views for SPA navigation
 * Sends page_view events directly to GA4 via gtag
 * Respects user consent (trusted v2 record only)
 *
 * Ownership contract: the GA4 loader in layout.tsx owns the INITIAL
 * page_view (sent in script.onload, the only point where gtag config
 * is guaranteed ready). This component owns ROUTE-CHANGE page_views
 * only and must never fire on first mount — doing so double-counts
 * the landing page for returning consented visitors, or gets dropped
 * pre-config depending on load timing. GA4 Enhanced Measurement
 * history-based page views are OFF; these two senders are the only
 * page_view sources.
 *
 * /admin (and every /admin/*) suppresses route-change GA4 page_views.
 * Navigating back OUT to a non-admin route resumes
 * normal tracking since the guard below only skips the admin pathname
 * itself, not the tracker's ongoing state.
 */
const ADMIN_PREFIX = '/admin'

function isAdminPath(pathname: string): boolean {
  return pathname === ADMIN_PREFIX || pathname.startsWith(`${ADMIN_PREFIX}/`)
}

export default function Ga4SpaTracking() {
  const pathname = usePathname()
  // Seeded with the landing pathname so the first-mount effect run is
  // a no-op; only genuine pathname changes pass the guard below.
  const lastTrackedPathname = useRef(pathname)

  useEffect(() => {
    if (typeof window === 'undefined' || !pathname) return

    // Skip first mount (and StrictMode re-runs): the layout loader
    // already sends the initial page_view for this pathname
    if (pathname === lastTrackedPathname.current) return
    lastTrackedPathname.current = pathname

    // Suppress route-change GA4 page_views on /admin
    if (isAdminPath(pathname)) return

    // Only send after consent granted
    if (!isAnalyticsConsentGranted()) return

    // Send page_view directly to GA4 if gtag is available
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_location: window.location.href,
        page_path: pathname,
        page_title: document.title,
      })
    }

    // Clarity auto-tracks navigation, no manual update needed
  }, [pathname])

  return null
}
